import { FocusKeyManager, FocusOrigin } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  QueryList
} from '@angular/core';

import { Subject, fromEvent, takeUntil } from 'rxjs';
import { Key } from 'ts-key-enum';

import { OptionComponentAbstract } from './option-component.abstract';

export type SelectedListboxOptions = string[];

@Component({
  selector: 'shrd-uic-listbox',
  templateUrl: './listbox.component.html',
  styleUrls: ['./listbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListboxComponent implements AfterViewInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  private readonly unsubscribeEventListeners$ = new Subject<void>();

  keyboardEventsManager: FocusKeyManager<OptionComponentAbstract>;
  focusedElementId: string;

  @Input() multiple = false;
  @Input() allowDeselect = true;
  @Input() hasHorizontalScroll = false;
  @Input() label = 'Please select';

  @Output() optionsChanged = new EventEmitter();
  @Output() initialized = new EventEmitter<ListboxComponent>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur = new EventEmitter<void>();

  @ContentChildren(OptionComponentAbstract)
  optionComponents: QueryList<OptionComponentAbstract>;

  @HostBinding('attr.shrd-uic-listbox') baseClass = true;
  @HostBinding('attr.role') role = 'listbox';
  @HostBinding('attr.tabindex') @Input() tabindex = 0;
  @HostBinding('attr.aria-activedescendant') get ariaActiveDescendant() {
    return this.focusedElementId;
  }
  @HostBinding('attr.aria-label') get ariaLabel() {
    return this.label;
  }
  @HostBinding('attr.aria-multiselectable') get ariaMultiselectable() {
    return this.multiple;
  }

  ngAfterViewInit(): void {
    this.initEventListener();
    this.updateOptionComponents();
    this.initKeyboardEventsManager();
    /* This is a workaround for getting access to the listbox in a ngbPopover */
    this.initialized.emit(this);
  }

  /**
   * Toggles selected state of all non-disabled options and emits change event.
   * Only supported in multi-select mode.
   */
  public toggleSelectAllOptions(): void {
    if (this.multiple) {
      const options = this.optionComponents.toArray().filter(option => !option.isDisabled);
      const state = options.length > options.filter(option => option.selected).length;
      options.forEach(option => (option.selected = state));
      this.optionsChanged.emit(this.getSelectedOptionValues());
    }
  }

  focusSelection(focusOrigin: FocusOrigin): void {
    this.scrollToSelectedItem(['keyboard', 'mouse', 'program'].includes(focusOrigin));
  }

  scrollToSelectedItem(focusElement = true): void {
    this.focusSelectedOption();
    if (!focusElement) {
      this.keyboardEventsManager.activeItem.blur();
    }
  }

  focusSelectedOption(): void {
    const selectedOption = this.optionComponents.toArray().findIndex(o => o.selected);
    /* If there is a selected option focus it */
    if (selectedOption >= 0) {
      this.keyboardEventsManager.setActiveItem(selectedOption);
      /* Else focus first option in list */
    } else {
      this.keyboardEventsManager.setFirstItemActive();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribeEventListeners$.next();
    this.unsubscribeEventListeners$.complete();
  }

  private initEventListener(): void {
    this.optionComponents.forEach(oc => {
      fromEvent(oc.nativeElement, 'click')
        .pipe(takeUntil(this.unsubscribeEventListeners$))
        .subscribe(() => this.onOptionClick(oc));

      fromEvent(oc.nativeElement, 'keydown')
        .pipe(takeUntil(this.unsubscribeEventListeners$))
        .subscribe((event: KeyboardEvent) => {
          this.hasHorizontalScroll && this.keyboardEventsManager.setActiveItem(oc);
          this.onKeydown(event);
        });
    });
  }

  private updateOptionComponents(): void {
    this.optionComponents.changes.subscribe(queryList => {
      if (queryList.length > 0) {
        this.unsubscribeEventListeners$.next();
        this.initEventListener();
        if (this.hasHorizontalScroll) {
          this.focusSelectedOption();
        }
      }
    });
  }

  private initKeyboardEventsManager(): void {
    this.keyboardEventsManager = new FocusKeyManager(this.optionComponents);

    this.keyboardEventsManager.change.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.focusedElementId = this.keyboardEventsManager.activeItem.id;
    });

    this.keyboardEventsManager.tabOut.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.blur.emit();
    });
  }

  private onKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const manager = this.keyboardEventsManager;

    switch (key) {
      case ' ':
      case Key.Enter:
        this.toggleFocusedOption();
        event.preventDefault();
        break;
      default:
        manager.onKeydown(event);
    }
  }

  private onOptionClick(option: OptionComponentAbstract): void {
    if (!option.disabled) {
      this.toggleOption(option);
    }
  }

  private toggleOption(option: OptionComponentAbstract): void {
    if (!this.allowDeselect && option.selected) {
      this.blur.emit();
      return;
    }

    option.selected = !option.selected;

    /* If not multiple reset all other selects */
    if (!this.multiple && option.selected) {
      this.optionComponents.forEach(oc => {
        if (oc.id !== option.id) {
          oc.selected = false;
        }
      });
    }

    this.optionsChanged.emit(this.getSelectedOptionValues());
  }

  private getSelectedOptionValues(): SelectedListboxOptions {
    return this.optionComponents.toArray().reduce((acc, oc) => {
      if (oc.selected) {
        acc.push(oc.value);
      }
      return acc;
    }, []);
  }

  private toggleFocusedOption(): void {
    const focusedIndex = this.keyboardEventsManager.activeItemIndex;
    if (focusedIndex !== null && this.isValidIndex(focusedIndex)) {
      const focusedOption = this.optionComponents.toArray()[focusedIndex];

      if (focusedOption && !focusedOption.disabled) {
        this.toggleOption(focusedOption);
      }
    }
  }

  private isValidIndex(index: number): boolean {
    return index >= 0 && index < this.optionComponents.length;
  }
}
